import { LazoPaths } from "@/enums/LazoPaths";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import { produce } from "immer";
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

type MapConversationsPerPage = {
  [k: string]: Partial<LazoChatConversation> | undefined;
};

const INITIAL_CONVERSATIONS_PER_PAGE = {
  [LazoPaths.DASHBOARD]: undefined,
  [LazoPaths.FUNDRAISING]: undefined,
};

export enum ConversationStateChange {
  CONVERSATION_CREATED = "CONVERSATION_CREATED",
  CONVERSATION_SELECTED = "CONVERSATION_SELECTED",
  CONVERSATION_RESET = "CONVERSATION_RESET",
}

interface SelectedConversationContextType {
  selectedConversation: LazoChatConversation | undefined;
  setSelectedConversation: (conversation: Partial<LazoChatConversation>, eventType?: ConversationStateChange, pathname?: LazoPaths) => void;
  resetSelectedConversation: () => void;
  restoreCurrentPageConversation: (path: string) => void;
}

const SelectedConversationContext = createContext<SelectedConversationContextType | undefined>(undefined);

interface SelectedConversationProviderProps {
  children: ReactNode;
}

export const SelectedConversationProvider: React.FC<SelectedConversationProviderProps> = ({ children }) => {
  const [selectedConversation, setSelectedConversationState] = useState<LazoChatConversation | undefined>(undefined);

  const [conversationsPerPage, setConversationPerPageState] = useState<MapConversationsPerPage>({ ...INITIAL_CONVERSATIONS_PER_PAGE });

  const setConversationPerPage = (k: string, conversation: Partial<LazoChatConversation>) => {
    setConversationPerPageState(
      produce((draft) => {
        draft[k] = conversation;
        return draft;
      })
    );
  };

  const restoreCurrentPageConversation = (path: string) => {
    const pathConversation = conversationsPerPage[path];
    if (!pathConversation) setSelectedConversation(undefined);
    setSelectedConversation(pathConversation);
  };

  const onConversationChange = (
    conversation: LazoChatConversation,
    eventType: ConversationStateChange = ConversationStateChange.CONVERSATION_SELECTED,
    pathname?: string
  ) => {
    setSelectedConversationState(conversation);
    if (pathname) {
      setConversationPerPage(pathname, conversation);
    } else {
      setConversationPerPage(location.pathname + location.search, conversation);
    }
    document.dispatchEvent(new CustomEvent(eventType, { detail: conversation }));
  };

  const setSelectedConversation = (
    conversation: Partial<LazoChatConversation>,
    eventType: ConversationStateChange = ConversationStateChange.CONVERSATION_SELECTED,
    pathname?: string
  ) => {
    // Update conversation in case it exists
    if (conversation) {
      LazoChatConversationService.getAndConsumeMessages(conversation.externalId!).then((r) => {
        r.onSuccess((c) => onConversationChange(c, eventType, pathname));
        r.onError(() => onConversationChange(conversation as LazoChatConversation, eventType, pathname)); // Ignore update errors
      });
    } else {
      onConversationChange(conversation as LazoChatConversation, eventType, pathname);
    }
  };

  const resetSelectedConversation = useCallback(() => {
    setSelectedConversationState(undefined);
    setConversationPerPage(location.pathname + location.search, undefined);
    document.dispatchEvent(new CustomEvent(ConversationStateChange.CONVERSATION_RESET));
  }, [selectedConversation]);

  const contextValue: SelectedConversationContextType = useMemo(() => {
    return {
      selectedConversation,
      setSelectedConversation,
      resetSelectedConversation,
      restoreCurrentPageConversation,
    };
  }, [selectedConversation, resetSelectedConversation]);

  return <SelectedConversationContext.Provider value={contextValue}>{children}</SelectedConversationContext.Provider>;
};

export const useSelectedConversation = (): SelectedConversationContextType => {
  const context = useContext(SelectedConversationContext);

  if (!context) {
    throw new Error("useSelectedConversation must be used within a SelectedConversationProvider");
  }

  return context;
};
